import { Box, Button, FormControl, FormLabel, HStack, Text } from "@chakra-ui/react";
import { AlertsContext } from "providers/alerts";
import { LoanContext } from "providers/loan";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useEffect, useState } from "react";

import { apiRequest } from "utils/api";
import { fullNumberToString, stringToDollar, stringToFullNumber } from "utils/helpers";
import MoneyField from "../MoneyInput";
import FundsRequested from "./FundsRequested";
import FundsVerified from "./FundsVerified";
import NoRequest from "./NoRequest";
import { isPermissionEnabled, permissionConstants, hasPermission } from "utils/permissionHelper";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { getErrorMessage } from "utils/errorHandler";

const ProofFundsCollapse = () => {
  const { user } = useTenant();
  const { loan } = useContext(LoanContext);
  const [amount, setAmount] = useState<string | number>("");
  const [requested, setRequested] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showError, showSuccess } = useContext(AlertsContext);
  const [readLoan, setReadLoan] = useState(false);

  const readOnlyLoan = () => {
    if (isPermissionEnabled(user) && hasPermission(user, permissionConstants.readOnlyLoans)) {
      setReadLoan(true);
    }
  };

  useEffect(() => {
    readOnlyLoan();
  }, []);

  async function sendRequest() {
    try {
      if (!stringToFullNumber(amount.toString())) {
        return showError({
          title: "Enter Amount",
          description: "Please enter a proof of funds amount.",
        });
      }
      setLoading(true);
      await apiRequest("/supabase/events/insert", {
        name: "requested_funds",
        loan_id: loan!.id,
        data: {
          amount: stringToFullNumber(amount.toString()),
        },
      });
      showSuccess({
        title: "Request Sent",
        message: "The proof of funds request was successfully sent.",
      });
      setRequested(true);
    } catch (err) {
      showError({
        title: "Request Failed",
        description: getErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (loan!.funds_requested) {
      setRequested(true);
      setAmount(fullNumberToString(loan!.funds_requested));
    }
  }, [loan]);

  if (loan!.funds_verified) {
    return <FundsVerified />;
  }

  return (
    <Box>
      {user.type === "lender" ? (
        <Box mt="16px">
          {requested ? (
            <Box>
              <Text mb="0px" fontSize="13px" lineHeight="24px">
                A proof of funds request for <Text as="b">{stringToDollar(amount.toString())}</Text>{" "}
                has been sent.
              </Text>
              <Button
                onClick={() => setRequested(false)}
                isDisabled={readLoan}
                variant="secondary"
                w="100%"
                mt="12px"
              >
                Edit Request
              </Button>
            </Box>
          ) : (
            <Box>
              <Text mb="0px" fontSize="13px" lineHeight="24px">
                You can request proof of funds from the borrower or guarantor.
              </Text>
              <FormControl mt="16px">
                <FormLabel>Requested Amount</FormLabel>
                <HStack spacing="12px">
                  <MoneyField
                    value={amount}
                    onValueChange={value => setAmount(value ?? "")}
                    disabled={requested || readLoan}
                  />
                  <Button
                    isLoading={loading}
                    isDisabled={readLoan}
                    variant={getCustomButtonVariant(user)}
                    minW="200px"
                    onClick={sendRequest}
                  >
                    Send Request
                  </Button>
                </HStack>
              </FormControl>
            </Box>
          )}
        </Box>
      ) : loan!.funds_requested ? (
        <FundsRequested />
      ) : (
        <NoRequest />
      )}
    </Box>
  );
};

export default ProofFundsCollapse;
